import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import moment from 'moment'
import type { Moment } from 'moment'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { ReportType } from '@/app/constants/reportType'
import { getReportNumberByType } from '@/app/services/printout/reportTypeResolver'
import { withPrintoutData } from '@/modules/printouts/utils/reportDefaultParams'
import { OutputType } from '@/app/constants/outputType'
import { v4 as uuidv4 } from 'uuid'
import { SERVICE_TIME_FORMAT } from '@/app/constants/date'
import { formatDate } from '@/modules/shared/utils/date'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePrintJobStore } from '@/modules/printouts/stores/printJobStore'

interface GeneratePrintoutBaseParams {
  companyId: string
  reportTypes: ReportType[]
  driverIds: string[]
  dateFrom: string|Date
  dateTo: string|Date
  generateToSeparateFiles: boolean
  rememberSelected: boolean
  issuerSignature?: boolean
  driverSignature?: boolean
  outputType?: OutputType
}

interface GeneratePrintoutParams {
  companyId: string
  reportTypes: ReportType[]
  driverIds: string[]
  month: number
  year: number
  generateToSeparateFiles: boolean
  rememberSelected: boolean
  outputType?: OutputType
}

const usePrintService = () => {
  const loading = useLoadingStore(selectLoading)
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { activeLanguage } = useLocalize()

  const generatePrintoutForDateRange = async ({
    companyId,
    reportTypes,
    driverIds,
    dateFrom,
    dateTo,
    generateToSeparateFiles,
    rememberSelected,
    issuerSignature = true,
    driverSignature = true,
    outputType = OutputType.Pdf,
  }: GeneratePrintoutBaseParams) => {
    const stopLoading = startLoading()
    const finishJob = usePrintJobStore.getState().beginJob()
    try {
      // Convert driver IDs from strings to numbers (API expects numbers)
      const driverNumbers = driverIds
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))

      if (driverNumbers.length === 0) {
        throw new Error('No valid driver IDs provided')
      }

      if (reportTypes.length === 0) {
        throw new Error('No report types selected')
      }

      const startTime = formatDate(dateFrom, SERVICE_TIME_FORMAT)

    
      const endTimeRaw = formatDate(dateTo, SERVICE_TIME_FORMAT)
      const endTime = endTimeRaw.endsWith(' 00:00')
        ? `${endTimeRaw.slice(0, -5)}23:59`
        : endTimeRaw

      // Generate request ID
      const requestId = uuidv4()

      // JEDNO zadanie na cala paczke raportow.
      //
      // Wczesniej szlo tu N zadan - po jednym na typ raportu - wszystkie z tym samym
      // `id`. Serwer tworzy zadanie po tym wlasnie id, wiec przechodzilo pierwsze, a
      // reszte odrzucal jako duplikat: z pieciu zaznaczonych raportow powstawal jeden,
      // po cichu. Serwer przyjmuje liste w `reportTypes` i zapisuje ja jako tablice
      // konfiguracji, ktora GenerateReportJob umial czytac od poczatku.
      const reportNumbers = reportTypes.map((reportType) =>
        getReportNumberByType(reportType),
      )

      const payload = {
        id: requestId,
        drivers: driverNumbers,
        vehicles: null,
        // Zgodnosc wstecz: pojedynczy `reportType` dla starszych sciezek serwera.
        reportType: reportNumbers[0],
        reportTypes: reportNumbers,
        startTime: startTime,
        endTime: endTime,
        outputType,
        data: {
          separateFiles: generateToSeparateFiles,
          rememberSelected,
          issuerSignature,
          driverSignature,
          // Raport kosztów z ewidencji: delegacje rozpoczęte w okresie, jak w WPF.
          ...(reportTypes.includes(ReportType.TRIP_COSTS)
            ? { tripsStartedInPeriod: true }
            : {}),
        },
        language: activeLanguage?.code,
      }

      const response = await axiosInstance.post(
        `/print-api/${companyId}/printout/print`,
        payload,
      )

      const responseData = response?.data

      // Extract task ID from response
      // The response format may vary, so we'll try to extract it
      let taskId = requestId
      if (responseData?.id) {
        taskId = responseData.id
      } else if (responseData?.data?.id) {
        taskId = responseData.data.id
      } else if (responseData?.taskId) {
        taskId = responseData.taskId
      }

      usePrintJobStore.getState().trackTask(taskId)

      return {
        taskId,
        requestId,
        success: true,
      }
    } catch (err: any) {
      console.error('Error generating printout:', err)
      throw new Error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to generate printout',
      )
    } finally {
      stopLoading()
      finishJob()
    }
  }

  const generatePrintout = async ({
    companyId,
    reportTypes,
    driverIds,
    month,
    year,
    generateToSeparateFiles,
    rememberSelected,
    outputType,
  }: GeneratePrintoutParams) => {
    const dateFrom = new Date(year, month - 1, 1)
    const dateTo = new Date(year, month, 1)
    dateTo.setDate(dateTo.getDate() - 1)

    return generatePrintoutForDateRange({
      companyId,
      reportTypes,
      driverIds,
      dateFrom,
      dateTo,
      generateToSeparateFiles,
      rememberSelected,
      outputType,
    })
  }

  const getPdf = async ({
    companyId,
    reportType,
    reportTypes,
    drivers,
    startTime,
    endTime,
    data,
    showLoading = true,
  }: {
    companyId: string
    reportType?: ReportType
    reportTypes?: ReportType[]
    drivers: Array<string | number>
    startTime: Moment | string
    endTime: Moment | string
    data?: any
    showLoading?: boolean
  }): Promise<string> => {
    const stopLoading = showLoading ? startLoading() : () => {}
    const finishJob = usePrintJobStore.getState().beginJob()
    try {
      const id = uuidv4()

      const startTimeStr =
        typeof startTime === 'string'
          ? decodeURIComponent(startTime)
          : moment(startTime).format(SERVICE_TIME_FORMAT)
      const endTimeStr =
        typeof endTime === 'string'
          ? decodeURIComponent(endTime)
          : moment(endTime).format(SERVICE_TIME_FORMAT)

      const driverNumbers = drivers.map((d) =>
        typeof d === 'string' ? parseInt(d, 10) : d,
      )

      const useBatch = reportTypes != null && reportTypes.length > 0

      const payload: Record<string, unknown> = {
        id,
        drivers: driverNumbers,
        vehicles: null,
        startTime: startTimeStr,
        endTime: endTimeStr,
        outputType: OutputType.Pdf,
        data:
          reportType == null
            ? data
            : withPrintoutData(
                reportType,
                (data ?? {}) as Record<string, unknown>,
              ),
        language: activeLanguage?.code,
      }

      if (useBatch) {
        const numbers = reportTypes!.map((rt) => getReportNumberByType(rt))
        payload.reportTypes = numbers
        payload.reportType = numbers[0]
      } else {
        if (reportType == null) {
          throw new Error('reportType is required when reportTypes is not provided')
        }
        payload.reportType = getReportNumberByType(reportType)
      }

      // Legacy task-based printout API (ten sam endpoint co m.in. wydruk tabel / harmonogramów).
      await axiosInstance.post(
        `print-api/${companyId}/printout/print`,
        payload,
      )

      usePrintJobStore.getState().trackTask(id)

      return id
    } catch (err) {
      console.error('Error generating PDF:', err)
      throw err
    } finally {
      stopLoading()
      finishJob()
    }
  }

  return {
    generatePrintoutForDateRange,
    generatePrintout,
    getPdf,
    loading,
  }
}

export default usePrintService

